import React from 'react';
import type { DepartmentPattern } from '../../types/aai';
import { Action, Chip, Divided, Drawer, EmptyState, Kicker, ListRow, RouteMark, StatusChip } from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';
import { plural } from './useDepartment';

/*
 * A shared pattern, opened in place. The count and the departments are
 * privacy-safe aggregates across requests; the only requests named are the
 * head's own department's, and each opens its Project record.
 */
export const PatternDrawer: React.FC<{
  pattern: DepartmentPattern;
  department: string;
  onOpenProject: (id: string) => void;
  onClose: () => void;
}> = ({ pattern, department, onOpenProject, onClose }) => (
  <Drawer
    onClose={onClose}
    kicker={<Kicker>Orbit findings</Kicker>}
    badge={<Chip tone="warn" dot>{plural(pattern.count, 'request', 'requests')}</Chip>}
    title={pattern.label}
    sub={`Counted across ${pattern.departments.join(', ')}. The requests themselves stay private to the people who raised them.`}
    footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
  >
    <Tiles
      cols={2}
      facts={[
        { label: 'Requests sharing it', value: `${pattern.count}` },
        { label: 'Departments', value: pattern.departments.join(' · ') },
        { label: `From ${department}`, value: `${pattern.requests.length}` },
        { label: 'Other departments', value: 'Counted, never named by request' },
      ]}
    />
    <div className="mt-6">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Your department&apos;s requests</div>
      {pattern.requests.length === 0 && <EmptyState title="None from your department" reason="This pattern is counted from other departments' requests. Their records are not yours to open." />}
      <Divided>
        {pattern.requests.map((r) => (
          <ListRow
            key={r.id}
            mark={<RouteMark kind="project" size="md" />}
            title={r.title}
            right={<StatusChip status={r.status} />}
            onClick={() => onOpenProject(r.id)}
            arrow
          />
        ))}
      </Divided>
    </div>
  </Drawer>
);
